/**
 * A paid plan paused for a failed payment, as the pages see it (2026-10-11, no grace — Miles).
 *
 * A plan payment that does not go through pauses new work at once: tasks already running finish, nothing is
 * deleted, and work resumes the moment a payment succeeds. A charge still unpaid UNPAID_DAYS_BEFORE_END days after it
 * failed ends the plan. The server owns all of it (functions/src/billing/billingPause.ts); in the same transaction
 * that pauses or lifts, it writes accounts/{accountId}/wallet/displaySummary.billing_pause = {paused, since_ms} —
 * the display mirror every page already listens to — and this module reads it.
 *
 * The words below are /subscription's, the same in both designs; the band every page shows owners and billing managers
 * keeps its own three lines (src/components/common/BillingPausedBanner.tsx). UNPAID_DAYS_BEFORE_END is the server's twin, pinned equal by
 * tests/billing/billingPauseTwin.test.ts.
 */

export const UNPAID_DAYS_BEFORE_END = 30;

export interface BillingPauseView {
  paused: boolean;
  /** When the failed charge paused work (epoch ms); null when not on record. */
  sinceMs: number | null;
}

/** The stored view, re-validated; null when absent or malformed. */
export function parseBillingPauseView(raw: unknown): BillingPauseView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.paused !== 'boolean') return null;
  const since = typeof r.since_ms === 'number' && Number.isFinite(r.since_ms) && r.since_ms > 0 ? r.since_ms : null;
  return { paused: r.paused, sinceMs: r.paused ? since : null };
}

/** The day an unpaid plan ends (epoch ms), when the pause's moment is known. */
export function billingPauseEndsAtMs(view: BillingPauseView | null | undefined): number | null {
  if (!view?.paused || view.sinceMs === null) return null;
  return view.sinceMs + UNPAID_DAYS_BEFORE_END * 86_400_000;
}

/** Is this plan status one a failed payment pauses? (The server's closed list, billingPause.ts.) */
export function isPausedPlanStatus(status: unknown): boolean {
  return status === 'past_due' || status === 'unpaid';
}

// copy: billing_paused_band
export const BILLING_PAUSE_COPY = {
  notice:
    'your last payment didn’t go through, so new work is paused. tasks already running finish, and nothing is ' +
    'deleted. retry the payment, or update the card and retry — work resumes the moment it succeeds.',
  noticeWorkspace:
    'the workspace’s last payment didn’t go through, so new work is paused. tasks already running finish, and ' +
    'nothing is deleted. work resumes the moment a payment succeeds.',
  endsOn: 'if it’s still unpaid, the plan ends on',
  endsAfter: `if it’s still unpaid ${UNPAID_DAYS_BEFORE_END} days after it failed, the plan ends.`,
  retry: 'retry payment',
  retrying: 'retrying…',
  paid: 'paid — work has resumed.',
  nothingOwed: 'nothing is owed now — work has resumed.',
  bankCheck: 'your bank wants to confirm this payment — finish the check to resume.',
};
